import { useState } from "react";
import { Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import * as Abas from "@radix-ui/react-tabs";
import { Download, Lock } from "lucide-react";
import type { LinhaDeCalculo, PagadorDaFatura, ResumoDeFatura } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import {
  useFaturas,
  usePessoas,
  usePrevia,
  useSistemas,
  type FiltrosDeFaturas,
} from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { salvar } from "@/api/salvar";
import { Tabela, type Coluna } from "@/componentes/Tabela";
import { Botao } from "@/componentes/Botao";
import { Pilula } from "@/componentes/PilulaDeSituacao";
import { Dica } from "@/componentes/Dica";
import { PainelLateral } from "@/componentes/PainelLateral";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { useAvisos } from "@/componentes/Avisos";
import { competencia, contagem, dia, dinheiro, quantidade } from "@/formato";
import { rotaDaFatura } from "@/rotas";
import { franquia } from "@/telas/planos/plano";
import { FechamentoDeCompetencia } from "./FechamentoDeCompetencia";
import { ItensDaFatura } from "./ItensDaFatura";
import { SITUACOES_DE_FATURA, aparenciaDaFatura, nomeDoPagador, somaDos } from "./fatura";

/*
 * Faturas — change `add-faturamento`, design D8.
 *
 * Duas abas: a PRÉVIA da competência em andamento, calculada sob demanda e
 * nunca gravada, e as FECHADAS, que o fechamento gravou e que só mudam de
 * situação. Aba e filtros vivem na URL, pelo motivo de Boletos: o link que um
 * operador manda é a tela que o outro abre.
 *
 * Aplicação e cedente valem nas duas abas; competência, situação e "vencidas"
 * são da lista gravada — a prévia é sempre do mês corrente, e não tem situação.
 *
 * `/faturas/:id` abre sobre as Fechadas, mesmo sem `?aba=` no link colado: só fatura
 * gravada tem identificador. A prévia é cálculo, e o painel dela continua local.
 */
const ABAS = [
  ["previa", "Prévia do mês"],
  ["fechadas", "Fechadas"],
] as const;

const CONTROLE =
  "h-8 rounded-controle border border-fio bg-superficie-2 px-2 text-corpo text-texto";

export function Faturas() {
  const { cliente, estado, pode } = useSessao();
  const [parametros, setParametros] = useSearchParams();
  const { id: aberta } = useParams();
  const [fechando, setFechando] = useState(false);

  const aba = aberta !== undefined || parametros.get("aba") === "fechadas" ? "fechadas" : "previa";
  const dentro = estado.fase === "dentro";

  // As listas do filtro são as do cadastro, já em cache nas outras telas.
  const { data: sistemas } = useSistemas(cliente, dentro);
  const { data: pessoas } = usePessoas(cliente, dentro);

  function ajustar(chave: string, valor: string) {
    const proximos = new URLSearchParams(parametros);
    if (valor === "") proximos.delete(chave);
    else proximos.set(chave, valor);
    setParametros(proximos, { replace: true });
  }

  const doPagador = {
    sistema_id: parametros.get("sistema_id") ?? undefined,
    pessoa_id: parametros.get("pessoa_id") ?? undefined,
  };
  const filtros: FiltrosDeFaturas = {
    ...doPagador,
    competencia: parametros.get("competencia") ?? undefined,
    situacao: parametros.get("situacao") ?? undefined,
    vencidas: parametros.get("vencidas") === "true" ? "true" : undefined,
  };

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-titulo font-semibold">Faturas</h1>

        {/* Some para quem não é `admin`: a API recusaria com `403`. */}
        {pode("admin") && (
          <Botao className="ml-auto" onClick={() => setFechando(true)}>
            <Lock aria-hidden="true" className="size-4" />
            Fechar competência
          </Botao>
        )}
      </header>

      {fechando && <FechamentoDeCompetencia aoFechar={() => setFechando(false)} />}

      <Abas.Root value={aba} onValueChange={(valor) => ajustar("aba", valor === "previa" ? "" : valor)}>
        <Abas.List aria-label="Faturas" className="flex flex-wrap gap-1 border-b border-fio">
          {ABAS.map(([valor, rotulo]) => (
            <Abas.Trigger
              key={valor}
              value={valor}
              className="-mb-px border-b-2 border-transparent px-3 py-2 text-corpo text-texto-2 data-[state=active]:border-esferografica data-[state=active]:text-texto"
            >
              {rotulo}
            </Abas.Trigger>
          ))}
        </Abas.List>

        <div className="flex flex-wrap items-center gap-2 pt-3">
          <select
            aria-label="Filtrar por aplicação"
            value={filtros.sistema_id ?? ""}
            onChange={(evento) => ajustar("sistema_id", evento.target.value)}
            className={CONTROLE}
          >
            <option value="">Todas as aplicações</option>
            {(sistemas?.sistemas ?? []).map((sistema) => (
              <option key={sistema.id} value={sistema.id}>
                {sistema.nome}
              </option>
            ))}
          </select>

          <select
            aria-label="Filtrar por cedente"
            value={filtros.pessoa_id ?? ""}
            onChange={(evento) => ajustar("pessoa_id", evento.target.value)}
            className={CONTROLE}
          >
            <option value="">Todos os cedentes</option>
            {(pessoas?.pessoas ?? []).map((pessoa) => (
              <option key={pessoa.id} value={pessoa.id}>
                {pessoa.razao_social}
              </option>
            ))}
          </select>

          {aba === "fechadas" && (
            <>
              <input
                type="month"
                aria-label="Competência"
                value={filtros.competencia ?? ""}
                onChange={(evento) => ajustar("competencia", evento.target.value)}
                className={CONTROLE}
              />

              <select
                aria-label="Filtrar por situação"
                value={filtros.situacao ?? ""}
                onChange={(evento) => ajustar("situacao", evento.target.value)}
                className={CONTROLE}
              >
                {SITUACOES_DE_FATURA.map(([valor, rotulo]) => (
                  <option key={valor} value={valor}>
                    {rotulo}
                  </option>
                ))}
              </select>

              <label className="flex h-8 items-center gap-2 text-corpo text-texto-2">
                <input
                  type="checkbox"
                  checked={filtros.vencidas === "true"}
                  onChange={(evento) => ajustar("vencidas", evento.target.checked ? "true" : "")}
                />
                Só vencidas
              </label>
            </>
          )}
        </div>

        {/*
          Sem `forceMount`: cada aba só busca aberta. A prévia é cálculo sobre o
          consumo do mês inteiro, e montá-la junto com a lista faria cada visita
          à lista pagar por ela.
        */}
        <Abas.Content value="previa" className="pt-3">
          <AbaDaPrevia filtros={doPagador} />
        </Abas.Content>

        <Abas.Content value="fechadas" className="pt-3">
          <AbaDasFechadas filtros={filtros} />
        </Abas.Content>
      </Abas.Root>
    </section>
  );
}

/* ── Prévia ──────────────────────────────────────────────────────────────── */

function AbaDaPrevia({ filtros }: { filtros: Pick<FiltrosDeFaturas, "sistema_id" | "pessoa_id"> }) {
  const { cliente, estado } = useSessao();
  const { data, error, isPending } = usePrevia(cliente, filtros, estado.fase === "dentro");
  const [aberta, setAberta] = useState<LinhaDeCalculo | null>(null);

  if (error) return <Falha erro={error} />;

  const linhas = data?.contratos ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {data !== undefined && (
          <span className="dados text-[12px] text-texto-2">
            {competencia(data.competencia)} · até {dia(data.ate)} · {dinheiro(data.total_centavos)}
          </span>
        )}
        <Pilula matiz="transito">prévia</Pilula>
        <Dica texto="Calculada agora, sem gravar. Muda até o fechamento." />
      </div>

      <Tabela
        colunas={COLUNAS_DA_PREVIA}
        itens={linhas}
        carregando={isPending}
        chaveDe={(linha) => linha.contrato_id}
        selecionado={aberta?.contrato_id}
        aoAbrir={setAberta}
        vazio="Nenhum contrato faturável."
      />

      {aberta !== null && <DetalheDaPrevia linha={aberta} aoFechar={() => setAberta(null)} />}
    </div>
  );
}

function DetalheDaPrevia({ linha, aoFechar }: { linha: LinhaDeCalculo; aoFechar: () => void }) {
  return (
    <PainelLateral
      aberto
      aoFechar={aoFechar}
      descricao={`Prévia de ${nomeDoPagador(linha)}`}
      titulo={
        <div className="flex flex-col gap-1">
          <span className="truncate text-titulo font-semibold text-texto">{nomeDoPagador(linha)}</span>
          <span className="flex items-center gap-2">
            <span className="dados text-[12px] text-texto-2">{dinheiro(linha.total_centavos)}</span>
            <Pilula matiz="transito">prévia</Pilula>
          </span>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="grid grid-cols-2 gap-2">
          {linha.pessoa_nome !== null && (
            <CaixaDeCampo rotulo="Aplicação" className="col-span-2">
              {linha.sistema_nome}
            </CaixaDeCampo>
          )}
          <CaixaDeCampo rotulo="Plano" className="col-span-2">
            {linha.plano_nome}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Mensalidade" dados>
            {dinheiro(linha.mensalidade_centavos)}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Franquia" dados>
            {franquia(linha.franquia)}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Na franquia" dados>
            {quantidade(linha.emissoes_na_franquia)}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Excedentes" dados>
            {quantidade(linha.excedentes)}
          </CaixaDeCampo>
        </div>

        <section className="flex flex-col gap-2">
          <h3 className="rotulo">Itens</h3>
          <ItensDaFatura
            itens={linha.itens}
            diasNaCompetencia={linha.dias_na_competencia}
            totalCentavos={linha.total_centavos}
          />
        </section>
      </div>
    </PainelLateral>
  );
}

/* ── Fechadas ────────────────────────────────────────────────────────────── */

function AbaDasFechadas({ filtros }: { filtros: FiltrosDeFaturas }) {
  const { cliente, estado } = useSessao();
  const avisos = useAvisos();
  const { id: aberta } = useParams();
  const { search } = useLocation();
  const { data, error, isPending } = useFaturas(cliente, filtros, estado.fase === "dentro");
  const [exportando, setExportando] = useState(false);

  const faturas = data?.faturas ?? [];
  const filtrada = Object.values(filtros).some((valor) => valor !== undefined);

  /*
   * O CSV vem do servidor, com os MESMOS filtros da tela, e não é montado com
   * a página que está aqui: a página tem teto de 200, e a exportação existe
   * justamente para tirar tudo. O servidor neutraliza fórmula em célula de
   * texto (nome de aplicação é escrito por terceiro).
   */
  async function exportar() {
    setExportando(true);
    try {
      const arquivo = await cliente.baixar(
        "/admin/v1/faturas.csv",
        filtros.competencia ? `faturas-${filtros.competencia}.csv` : "faturas-todas.csv",
        { busca: filtros, aceita: "text/csv" },
      );
      salvar(arquivo.conteudo, arquivo.nome);
      if (arquivo.cabecalhos.get("x-exportacao-truncada") === "true") {
        avisos.feito("CSV exportado", "Só as 10.000 mais recentes.");
      } else {
        avisos.feito("CSV exportado");
      }
    } catch (falha) {
      avisos.falhou(textoDoErro(falha));
    } finally {
      setExportando(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {error ? (
        <Falha erro={error} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rotulo">
              {isPending ? "carregando" : contagem(faturas.length, "fatura", "faturas")}
              {faturas.length === 200 ? " · limite da página" : ""}
            </span>
            <Botao
              tom="secundario"
              className="ml-auto"
              carregando={exportando}
              onClick={() => void exportar()}
            >
              <Download aria-hidden="true" className="size-4" />
              Exportar CSV
            </Botao>
          </div>

          <Tabela
            colunas={COLUNAS_DAS_FECHADAS}
            itens={faturas}
            carregando={isPending}
            chaveDe={(fatura) => fatura.id}
            selecionado={aberta}
            hrefDe={(fatura) => rotaDaFatura(fatura.id, search)}
            rotuloDe={(fatura) => `${nomeDoPagador(fatura)}, ${competencia(fatura.competencia)}`}
            vazio={filtrada ? "Nenhuma fatura com esses filtros." : "Nenhuma fatura."}
          />
        </>
      )}

      {/*
        Fora do ramo do erro, e sempre no mesmo lugar da árvore: o painel lê por `id`, e uma
        releitura da lista que falha não pode remontá-lo com um modal aberto.
      */}
      <Outlet context={faturas} />
    </div>
  );
}

function Falha({ erro }: { erro: unknown }) {
  return (
    <p role="alert" className="rounded-cartao border border-falha/40 bg-falha/10 px-4 py-3 text-falha">
      {textoDoErro(erro)}
    </p>
  );
}

/* ── Colunas ─────────────────────────────────────────────────────────────── */

function colunaDoPagador<T extends PagadorDaFatura>(): Coluna<T> {
  return {
    chave: "pagador",
    rotulo: "Pagador",
    largura: "minmax(180px, 1.6fr)",
    render: (linha) => (
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate">{nomeDoPagador(linha)}</span>
        {/* O cedente paga; a aplicação dele vai embaixo, como dado. */}
        {linha.pessoa_nome !== null && (
          <span className="truncate text-[11px] text-texto-3">{linha.sistema_nome}</span>
        )}
      </div>
    ),
  };
}

/* Em 1024 fica o dinheiro: o que sai está no painel de detalhe da linha. */
const COLUNAS_DA_PREVIA: readonly Coluna<LinhaDeCalculo>[] = [
  colunaDoPagador<LinhaDeCalculo>(),
  {
    chave: "plano",
    rotulo: "Plano",
    largura: "minmax(140px, 1fr)",
    descarte: 2,
    render: (linha) => <span className="truncate text-texto-2">{linha.plano_nome}</span>,
  },
  {
    chave: "dias",
    rotulo: "Dias",
    largura: "72px",
    descarte: 1,
    direita: true,
    render: (linha) => (
      <span className="dados text-texto-2">{`${linha.dias_vigentes}/${linha.dias_na_competencia}`}</span>
    ),
  },
  {
    chave: "mensalidade",
    rotulo: "Mensalidade",
    largura: "112px",
    direita: true,
    render: (linha) => <span className="dados">{dinheiro(somaDos(linha.itens, "mensalidade"))}</span>,
  },
  {
    chave: "franquia",
    rotulo: "Na franquia",
    largura: "96px",
    descarte: 3,
    direita: true,
    render: (linha) => <span className="dados">{quantidade(linha.emissoes_na_franquia)}</span>,
  },
  {
    chave: "excedentes",
    rotulo: "Excedentes",
    largura: "96px",
    direita: true,
    render: (linha) => <span className="dados">{quantidade(linha.excedentes)}</span>,
  },
  {
    chave: "ajustes",
    rotulo: "Ajustes",
    largura: "104px",
    direita: true,
    render: (linha) => {
      const ajustes = somaDos(linha.itens, "ajuste");
      return (
        <span className={`dados ${ajustes < 0 ? "text-falha" : "text-texto-2"}`}>
          {ajustes === 0 ? "—" : dinheiro(ajustes)}
        </span>
      );
    },
  },
  {
    chave: "total",
    rotulo: "Parcial",
    largura: "120px",
    direita: true,
    render: (linha) => <span className="dados">{dinheiro(linha.total_centavos)}</span>,
  },
];

const COLUNAS_DAS_FECHADAS: readonly Coluna<ResumoDeFatura>[] = [
  {
    chave: "competencia",
    rotulo: "Competência",
    largura: "104px",
    render: (fatura) => <span className="dados">{competencia(fatura.competencia)}</span>,
  },
  colunaDoPagador<ResumoDeFatura>(),
  {
    chave: "plano",
    rotulo: "Plano",
    largura: "minmax(140px, 1fr)",
    descarte: 1,
    render: (fatura) => <span className="truncate text-texto-2">{fatura.plano_nome}</span>,
  },
  {
    chave: "total",
    rotulo: "Total",
    largura: "120px",
    direita: true,
    render: (fatura) => <span className="dados">{dinheiro(fatura.total_centavos)}</span>,
  },
  {
    chave: "vencimento",
    rotulo: "Vencimento",
    largura: "104px",
    direita: true,
    render: (fatura) => <span className="dados">{dia(fatura.vencimento)}</span>,
  },
  {
    chave: "situacao",
    rotulo: "Situação",
    largura: "112px",
    direita: true,
    render: (fatura) => {
      const { matiz, rotulo } = aparenciaDaFatura(fatura);
      return <Pilula matiz={matiz}>{rotulo}</Pilula>;
    },
  },
];
